import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { round } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import useUnitProvider from '/@/core/context/unit';
import { useCreateCropReport, useReports } from '/@/hooks/reports';
import { _ } from '/@/locale';
import { formatDate } from '/@/utils';

const usePendingReportsRetry = (query, intervalMs = 5000) => {
    useEffect(() => {
        const someIsPending = (query.data?.results || []).some((r) => r.status === 'pending');
        let interval: NodeJS.Timeout | undefined;
        if (someIsPending) {
            interval = setInterval(() => {
                query.mutate();
            }, intervalMs);
        }
        return () => clearInterval(interval);
    }, [query.data, intervalMs]);
};

export const useModalReportForm = ({
    field,
    cropMonitoring,
    type,
}: {
    field: any;
    cropMonitoring: any;
    type: 'fieldview' | 'manual';
}) => {
    const isFieldviewType = type === 'fieldview';
    const units = useUnitProvider();

    const form = useForm<{
        plantingDate: Dayjs | null;
        plantingResultId: number | null;
        plantingRequestId: number | null;
        harvestDate: Dayjs | null;
        harvestResultId: number | null;
        harvestRequestId: number | null;
        yield: number | null;
        humidity: number | null;
    }>({
        defaultValues: {
            humidity: 14.5,
            plantingDate: dayjs(cropMonitoring?.plantingDate),
        },
    });

    // Create/Regenerate Reports on mount
    const createApplicationReportMutation = useCreateCropReport({ field: field.id, type: 'application' });
    const createPlantingReportMutation = useCreateCropReport({ field: field.id, type: 'planting' });
    const createHarvestReportMutation = useCreateCropReport({ field: field.id, type: 'harvest' });
    const createReports = () => {
        createApplicationReportMutation.trigger();
        createPlantingReportMutation.trigger();
        createHarvestReportMutation.trigger();
    };
    useEffect(() => {
        if (isFieldviewType) createReports();
    }, [type]);

    // Harvest Date
    const harvestReportsQuery = useReports({ fieldId: field.id, type: 'harvest' }, { enabled: isFieldviewType });
    const validCornHarvestReportResults = useMemo(() => {
        return (harvestReportsQuery.data?.results || []).flatMap((report) =>
            report.results.filter(
                (result) => result.cropName === 'Corn' && dayjs().diff(dayjs(result.harvestDates[0]), 'month') <= 8
            )
        );
    }, [harvestReportsQuery.data]);
    const harvestDateOptions = useMemo(() => {
        const today = dayjs();
        return (validCornHarvestReportResults || [])
            .map((result) => {
                const firstHarvestDate = dayjs(result.harvestDates[0]);
                return {
                    value: result.id,
                    label: `${formatDate(firstHarvestDate.toDate())} (${_('Area cosechada:')} ${units.toArea(result.totalHarvestedArea, 2).toString()})`,
                    todayDiff: Math.abs(firstHarvestDate.diff(today, 'day')),
                    date: firstHarvestDate,
                    requestId: result.report,
                };
            })
            .toSorted((a, b) => a.todayDiff - b.todayDiff);
    }, [validCornHarvestReportResults]);
    usePendingReportsRetry(harvestReportsQuery);

    // Planting Date
    const harvestDate = form.watch('harvestDate');
    const plantingReportsQuery = useReports(
        { fieldId: field.id, type: 'planting' },
        {
            enabled: isFieldviewType,
        }
    );
    const validCornPlantingReportResults = useMemo(() => {
        return (plantingReportsQuery.data?.results || []).flatMap((report) =>
            report.mergedResults.filter(
                (result) => result.cropName === 'Corn' && dayjs().diff(dayjs(result.plantingDate), 'month') <= 8
            )
        );
    }, [plantingReportsQuery.data]);
    const plantingDateOptions = useMemo(() => {
        return (validCornPlantingReportResults || [])
            .map((result) => {
                const plantingDate = dayjs(result.plantingDate);
                return {
                    value: result.id,
                    label: `${formatDate(plantingDate.toDate())} (${_('Area plantada')}: ${units.toArea(result.plantedArea, 2).toString()}) (${result.seedProductNames.join(', ')})`,
                    harvestDateDiff: harvestDate ? harvestDate.diff(plantingDate, 'day') : 0,
                    date: plantingDate,
                    requestId: result.report,
                };
            })
            .toSorted((a, b) => a.harvestDateDiff - b.harvestDateDiff);
    }, [validCornPlantingReportResults, harvestDate]);
    usePendingReportsRetry(plantingReportsQuery);

    // Set default values
    useEffect(() => {
        const nearestDate = harvestDateOptions?.[0];
        if (nearestDate !== undefined) {
            form.setValue('harvestDate', nearestDate.date);
            form.setValue('harvestResultId', nearestDate.value);
            form.setValue('harvestRequestId', nearestDate.requestId);
        }
    }, [harvestDateOptions]); // Set nearest harvest option

    // Set planting date from cropMonitoring when no options available
    useEffect(() => {
        if (isFieldviewType && !plantingDateOptions.length && cropMonitoring?.plantingDate) {
            const plantingDate = dayjs(cropMonitoring.plantingDate);
            form.setValue('plantingDate', plantingDate);
        }
    }, [isFieldviewType, plantingDateOptions.length, cropMonitoring?.plantingDate]);

    useEffect(() => {
        const nearestDate = plantingDateOptions.filter((el) => el.harvestDateDiff >= 0)?.[0];
        if (nearestDate !== undefined) {
            form.setValue('plantingDate', nearestDate.date);
            form.setValue('plantingResultId', nearestDate.value);
            form.setValue('plantingRequestId', nearestDate.requestId);
        }
    }, [plantingDateOptions]); // Set nearest planting option

    // Yield and humidity
    const harvestResultId = form.getValues('harvestResultId');
    useEffect(() => {
        const result = validCornHarvestReportResults.find((r) => r.id === harvestResultId);
        if (result) {
            form.setValue('yield', units.toYield(result.climateCalculatedAverageYield, 'corn', 2).value);
            form.setValue('humidity', round(result.totalWetMass / result.totalMoistureMass, 2));
        }
    }, [harvestResultId, validCornHarvestReportResults]);

    return {
        plantingDateOptions,
        harvestDateOptions,
        form,
        fvError: createPlantingReportMutation.error || createHarvestReportMutation.error,
        createReports,
    };
};
